import { createInstance, type ParseKeys } from "i18next";
import { initReactI18next } from "react-i18next";

import cs from "./cs.json";
import en from "./en.json";

/**
 * UI language of the admin app itself ("cs"/"en"). Distinct from the
 * product's content locales in `lib/locales.ts` (BCP-47 whitelist for
 * the couples' wedding sites), which stay independent of this setting.
 */
export type UiLanguage = "cs" | "en";

/** Key type for `t()`, usable in module-level label maps (`labelKey: TranslationKey`). */
export type TranslationKey = ParseKeys;

/** Mirrors the "wedding-admin-theme" convention used by ThemeProvider. */
const STORAGE_KEY = "wedding-admin-lang";

export const UI_LANGUAGES: { code: UiLanguage; nativeName: string; flagTag: string }[] = [
  { code: "cs", nativeName: "Čeština", flagTag: "cs-CZ" },
  { code: "en", nativeName: "English", flagTag: "en-GB" },
];

function detectLanguage(): UiLanguage {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "cs" || stored === "en") return stored;
  } catch {
    // Storage unavailable (private mode etc.); fall through to browser language.
  }
  return navigator.language?.toLowerCase().startsWith("cs") ? "cs" : "en";
}

export function setUiLanguage(lang: UiLanguage): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Best effort; the in-memory language still switches.
  }
  void i18n.changeLanguage(lang);
}

// initReactI18next registers this instance as react-i18next's default,
// so `useTranslation()` works app-wide without a provider component.
const i18n = createInstance();
void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    cs: { translation: cs },
  },
  lng: detectLanguage(),
  fallbackLng: "en",
  supportedLngs: ["cs", "en"],
  interpolation: { escapeValue: false }, // React already escapes.
  returnNull: false,
  // Resources are bundled, so init synchronously; no Suspense needed.
  initAsync: false,
  react: { useSuspense: false },
});

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

export default i18n;
